import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Users, CheckCircle2, AlertTriangle, Clock, Image as ImageIcon, CloudUpload, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlaySyncPanel = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/play/sync-status`, { headers });
      setStatus(res.data);
    } catch (e) {
      toast.error('Error al cargar el estado de sincronización');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const run = async (key, url, successMsg) => {
    setAction(key);
    try {
      const res = await axios.post(`${API}${url}`, {}, { headers });
      toast.success(typeof successMsg === 'function' ? successMsg(res.data) : successMsg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ocurrió un error');
    } finally {
      setAction('');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return 'Nunca';
    try {
      return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const stat = (label, value, Icon, color) => (
    <Card className="card-viubux-dark border-white/10" data-testid={`play-stat-${label}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="gotham-bold text-2xl text-white leading-none">{value}</div>
          <div className="verdana-regular text-white/60 text-xs mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="play-sync-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-violet-400" /> Sincronización con ViubuxPlay
          </h2>
          <p className="verdana-regular text-white/60 text-sm">
            Estado del espejo de usuarios Connect → Play (registro, edición, eliminación y plan).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              data-testid="play-reachable-badge"
              className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 ${
                status.play_reachable ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'
              }`}
            >
              {status.play_reachable ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
              {status.play_reachable ? 'Play en línea' : 'Play sin conexión'}
            </span>
          )}
          <Button
            data-testid="play-refresh-btn"
            onClick={load}
            disabled={loading}
            variant="outline"
            className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {!status ? (
        <p className="text-white/60">Cargando...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stat('Total usuarios', status.total_users, Users, 'bg-violet-500/80')}
            {stat('Sincronizados', status.synced, CheckCircle2, 'bg-green-500/80')}
            {stat('Con fallo', status.failed, AlertTriangle, 'bg-red-500/80')}
            {stat('Sin sincronizar', status.never_synced, Clock, 'bg-slate-500/80')}
          </div>

          <Card className="card-viubux-dark border-white/10">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="text-white/70 text-sm flex items-center gap-2" data-testid="play-last-sync">
                <Clock className="w-4 h-4 text-white/40" />
                Última sincronización: <strong className="text-white">{fmtDate(status.last_sync_at)}</strong>
              </div>
              <div className="text-white/70 text-sm flex items-center gap-2" data-testid="play-base64-pending">
                <ImageIcon className="w-4 h-4 text-white/40" />
                Avatares base64 por migrar: <strong className="text-white">{status.base64_avatars_pending}</strong>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="play-backfill-btn"
              onClick={() =>
                run('backfill', '/admin/play/backfill-users', (d) =>
                  `Backfill: ${d.processed} procesados (${d.created} nuevos, ${d.updated} actualizados, ${d.failed} fallidos)`
                )
              }
              disabled={!!action}
              className="btn-viubux"
            >
              <CloudUpload className={`w-4 h-4 mr-2 ${action === 'backfill' ? 'animate-pulse' : ''}`} />
              {action === 'backfill' ? 'Sincronizando...' : 'Sincronizar todos'}
            </Button>

            <Button
              data-testid="play-retry-btn"
              onClick={() =>
                run('retry', '/admin/play/retry-failed', (d) =>
                  `Reintento: ${d.recovered} recuperados, ${d.still_failing} aún con fallo`
                )
              }
              disabled={!!action || status.failed === 0}
              variant="outline"
              className="border-orange-500/40 text-orange-300 hover:bg-orange-500/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${action === 'retry' ? 'animate-spin' : ''}`} />
              {action === 'retry' ? 'Reintentando...' : `Reintentar fallidos (${status.failed})`}
            </Button>

            <Button
              data-testid="play-migrate-avatars-btn"
              onClick={() =>
                run('migrate', '/admin/play/migrate-avatars', (d) =>
                  `Avatares: ${d.migrated} migrados, ${d.resynced} re-enviados, ${d.failed} fallidos`
                )
              }
              disabled={!!action || status.base64_avatars_pending === 0}
              variant="outline"
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            >
              <ImageIcon className={`w-4 h-4 mr-2 ${action === 'migrate' ? 'animate-pulse' : ''}`} />
              {action === 'migrate'
                ? 'Migrando...'
                : `Migrar avatares base64 (${status.base64_avatars_pending})`}
            </Button>
          </div>

          {status.failures && status.failures.length > 0 && (
            <Card className="card-viubux-dark border-red-500/20" data-testid="play-failures-list">
              <CardHeader className="pb-3">
                <CardTitle className="gotham-medium text-white text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" /> Usuarios con fallo de sincronización
                </CardTitle>
                <p className="text-white/50 text-xs">
                  Se reintentan automáticamente cada 5 minutos cuando Play responde.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {status.failures.map((f, i) => (
                  <div
                    key={f.user_id || f.email || i}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 bg-black/30 rounded-lg px-3 py-2"
                    data-testid={`play-failure-${i}`}
                  >
                    <span className="text-white text-sm">{f.email || f.user_id}</span>
                    <span className="text-white/40 text-xs">
                      {f.attempts} intento(s) · {f.error?.slice(0, 90)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PlaySyncPanel;
